import React, { useContext, useEffect } from 'react'
import { useSelector, shallowEqual } from 'react-redux'
import {
  Grid,
  Container,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
  Tooltip,
  useTheme,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { ArrowBack } from '@material-ui/icons'
import CloseIcon from '@material-ui/icons/Close'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'

import SimpleBar from 'simplebar-react'
import 'simplebar/dist/simplebar.min.css'

import ProductImageThumb from '~/components/ProductImageThumb'
import useViewportSize from '~/components/useViewportSize'
import { useLiterals } from '~/components/Literals'

import ListEcommerce from './ListEcommerce'
import ItemEcommerce from './ItemEcommerce'
import CardsEcommerce from './CardsEcommerce'

import { selectChart } from '~/selectors'
import { EcommerceContext } from './DrawerEcommerce'

const useStyles = makeStyles(() => ({
  title: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderBottom: '1px solid #E0E0E0',
  },
  prices: {
    padding: '8px 24px',
    display: 'flex',
    gap: 8,
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    backgroundColor: '#EEEBEB',
    padding: 8,
  },
}))

function EcommerceHeader({ classes, onClose, isMobile }) {
  const { getLiteral } = useLiterals()

  return (
    <Grid className={classes.title}>
      <Grid style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {isMobile && (
          <Tooltip title={getLiteral('1051')} placement="bottom">
            <IconButton aria-label={getLiteral('1051')} onClick={onClose} color="primary" size="small">
              <ArrowBack color="inherit" />
            </IconButton>
          </Tooltip>
        )}

        <Typography variant="h6" style={{ width: '100%', lineHeight: 1 }}>
          {getLiteral('1317')}
        </Typography>
      </Grid>

      {!isMobile && (
        <Tooltip title={getLiteral('1251')} placement="bottom">
          <IconButton aria-label={getLiteral('1251')} onClick={onClose} size="small">
            <CloseIcon color="inherit" />
          </IconButton>
        </Tooltip>
      )}
    </Grid>
  )
}

function EcommerceFooter({ classes, isMobile }) {
  const { getLiteral } = useLiterals()

  return (
    <Grid className={classes.footer} style={{ padding: isMobile ? '0 8px' : 8 }}>
      <InfoOutlinedIcon style={{ color: '#999', fontSize: 16 }} />
      <Typography variant="body1" style={{ fontSize: 12, padding: 8, marginLeft: -2 }}>
        {getLiteral('2392')}
      </Typography>
    </Grid>
  )
}

export default function EcommerceFound({
  isManually = false,
  loadingEcommerce,
  dataEcommerce,
  openEcommerce,
  configEcommerce,
  handleLogger,
  onClose,
}) {
  const [chart] = useSelector(selectChart, shallowEqual)
  const { data: dataChart } = chart

  const classes = useStyles()
  const theme = useTheme()
  const viewport = useViewportSize()
  const isMobile = viewport.width < theme.gofind.desktopMaxWidth

  const { getLiteral } = useLiterals()
  const {
    showOffers,
    totalOffers,
    storesManuals,
    isEnableAlertPrice,
    priceMonitoring,
    offerMinMax,
    filteredOffers,
    dataOffersFeatured,
    showProductValue,
    ...rest
  } = useContext(EcommerceContext)

  const regex = !showOffers ? /(?:de)|(?:US)\$|(?:R)\$|\$/g : /(?:US)\$|(?:R)\$|\$/g

  const color = configEcommerce?.theme?.colorAction || configEcommerce?.colorPrimary

  const chartEcommerce = {
    storesManuals,
    isEnableAlertPrice,
    showOffers,
    totalOffers,
    offerMinMax,
    ...rest,
  }

  const isProductHavePriceMonitored = priceMonitoring.filter((pm) => pm.gtin === openEcommerce?.gtin).length > 0

  const isRenderAreaChart = isEnableAlertPrice && isProductHavePriceMonitored && dataChart.length > 0

  const product = openEcommerce

  if (isManually) {
    return (
      <Grid id="ecommerce-found-manuals" style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <EcommerceHeader classes={classes} onClose={onClose} isMobile={isMobile} />

        <ListItem
          style={{
            padding: '8px 16px',
          }}
        >
          <ListItemAvatar style={{ textAlign: 'center', marginRight: 10 }}>
            <ProductImageThumb isAvatar product={{ imageurl: product?.imageurl }} />
          </ListItemAvatar>

          <ListItemText primary={product?.name} />
        </ListItem>

        {showProductValue ? (
          <Grid className={classes.prices}>
            <Typography>{`${totalOffers} ${getLiteral('2131').replace(regex, '')}`}</Typography>
            {showOffers && (
              <strong style={{ fontSize: 16 }}>
                {getLiteral('1206', [`R$ ${offerMinMax.min}`, `R$ ${offerMinMax.max}`])}
              </strong>
            )}
          </Grid>
        ) : null}

        <Container
          style={{
            padding: 0,
            borderTop: '2px solid #E0E0E0',
            borderBottom: '2px solid #E0E0E0',
            flexGrow: 1,
            overflow: 'scroll',
            overflowX: 'hidden',
            overflowY: 'auto',
          }}
        >
          <SimpleBar style={{ overflow: 'auto', height: '100%' }}>
            <List>
              {storesManuals?.map((store, index, arr) => (
                <ItemEcommerce
                  isManually
                  key={store.name}
                  store={store}
                  product={openEcommerce}
                  handleLogger={handleLogger}
                  divider={index !== arr.length - 1}
                  config={configEcommerce}
                />
              ))}
            </List>
          </SimpleBar>
        </Container>

        {isRenderAreaChart && <CardsEcommerce color={color} language={configEcommerce?.language} {...chartEcommerce} />}

        <EcommerceFooter classes={classes} isMobile={isMobile} />
      </Grid>
    )
  }

  return (
    <Grid id="ecommerce-found-offers" style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <EcommerceHeader classes={classes} onClose={onClose} isMobile={isMobile} />

      {dataEcommerce?.single && (
        <ListItem
          style={{
            padding: '8px 16px',
          }}
        >
          <ListItemAvatar style={{ textAlign: 'center', marginRight: 10 }}>
            <ProductImageThumb product={{ imageurl: dataEcommerce.imageurl }} isAvatar={true} />
          </ListItemAvatar>

          <ListItemText primary={dataEcommerce.name} />
        </ListItem>
      )}

      {showProductValue ? (
        <Grid className={classes.prices}>
          <Typography>{`${totalOffers} ${getLiteral('2131').replace(regex, '')}`}</Typography>
          {showOffers && (
            <strong style={{ fontSize: 16 }}>
              {getLiteral('1206', [`R$ ${offerMinMax?.min}`, `R$ ${offerMinMax?.max}`])}
            </strong>
          )}
        </Grid>
      ) : null}

      <Container
        style={{
          padding: 0,
          borderTop: '2px solid #E0E0E0',
          borderBottom: '2px solid #E0E0E0',
          flexGrow: 1,
          overflow: 'scroll',
          overflowX: 'hidden',
          overflowY: 'auto',
        }}
      >
        {!loadingEcommerce && !dataEcommerce && (
          <Typography variant="h5" style={{ textAlign: 'center', width: '100%', position: 'fixed' }}>
            {getLiteral('2023')}
          </Typography>
        )}

        <SimpleBar style={{ overflow: 'auto', height: '100%' }}>
          <List style={{ padding: 0 }}>
            {dataEcommerce?.single && (
              <ListEcommerce
                configEcommerce={configEcommerce}
                openEcommerce={openEcommerce}
                handleLogger={handleLogger}
              />
            )}
          </List>
        </SimpleBar>
      </Container>

      {isRenderAreaChart && <CardsEcommerce product={product} color={color} {...chartEcommerce} />}

      <EcommerceFooter classes={classes} isMobile={isMobile} />
    </Grid>
  )
}
